/** Suport (App-Suport.dc.html) — conversații stil chat, FAQ acordeon, formular „Scrie-ne". */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSupportThread, useSupportThreads, useMutation, useInvalidate, qk } from '../api/hooks';
import { createSupportThread, markSupportRead, replySupport } from '../api/endpoints';
import { humanError } from '../api/client';
import { useToast } from '../context/ToastContext';
import { EmptyState, Modal, PageHeader, Skeleton, Spinner } from '../components/ui';
import { timeAgo } from '../lib/format';

const SUBJECTS = [
  { value: 'clip', label: 'Un clip' },
  { value: 'comanda', label: 'O comandă' },
  { value: 'facturare', label: 'Facturare' },
  { value: 'altceva', label: 'Altceva' },
];

const FAQ = [
  { q: 'Cât durează un clip Premium?', a: 'Aproximativ 10 minute din momentul în care aprobi imaginile. Primești notificare când e gata.' },
  { q: 'Când se consumă un clip din sold?', a: 'Exact la aprobarea imaginilor, când pornește generarea video. Până atunci iterezi gratuit.' },
  { q: 'Expiră clipurile din sold?', a: 'Nu. Clipurile cumpărate rămân în sold și nu expiră niciodată.' },
  { q: 'Ce înseamnă Ultra?', a: 'Un editor uman finisează clipul în 12–48h, cu revizii incluse.' },
];

export function SuportPage() {
  const { id } = useParams<{ id: string }>();
  if (id) return <ThreadView id={id} />;
  return <SupportList />;
}

function SupportList() {
  const navigate = useNavigate();
  const threads = useSupportThreads();
  const [newOpen, setNewOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    document.title = 'Suport — Reclame Video';
  }, []);

  const items = threads.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Suport"
        subtitle="Scrie-ne oricând — răspundem în conversații stil chat. Pentru urgențe, ai și WhatsApp."
        actions={<button className="btn btn-primary btn-sm" data-testid="new-thread" onClick={() => setNewOpen(true)}>+ Scrie-ne</button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 24, alignItems: 'start' }} className="dash-grid">
        <div>
          <h2 className="bricolage" style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>Conversațiile tale</h2>
          {threads.isLoading ? (
            <Skeleton height={120} />
          ) : items.length === 0 ? (
            <EmptyState illustration="💬" title="Nicio conversație" body="Ai o întrebare? Deschide un fir — îți răspundem repede." cta={<button className="btn btn-primary" onClick={() => setNewOpen(true)}>Scrie-ne</button>} />
          ) : (
            <div className="card" style={{ padding: 4 }} data-testid="support-threads">
              {items.map((t) => (
                <button key={t.id} onClick={() => navigate(`/suport/${t.id}`)} style={{ width: '100%', textAlign: 'left', padding: '14px 12px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 800, fontSize: 14 }}>{t.subject}</p>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{t.status === 'raspuns_primit' ? 'Ai un răspuns nou' : t.status === 'rezolvat' ? 'Rezolvat' : 'Deschis'} · {timeAgo(t.lastMessageAt || t.createdAt)}</span>
                  </div>
                  {(t.unreadClient ?? 0) > 0 && <span style={{ background: 'var(--accent)', color: 'var(--accent-ink)', borderRadius: 999, fontSize: 11, fontWeight: 800, padding: '2px 8px' }}>{t.unreadClient}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <aside style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card" style={{ padding: 16 }}>
            <h3 className="bricolage" style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Întrebări frecvente</h3>
            {FAQ.map((f, i) => (
              <div key={i} style={{ borderTop: i ? '1px solid var(--line)' : 'none' }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: '100%', textAlign: 'left', padding: '11px 0', fontWeight: 700, fontSize: 13.5, display: 'flex', gap: 8 }}>
                  <span style={{ flex: 1 }}>{f.q}</span>
                  <span aria-hidden="true" style={{ color: 'var(--muted)' }}>{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <p style={{ margin: '0 0 11px', fontSize: 13, color: 'var(--muted)', lineHeight: 1.55 }}>{f.a}</p>}
              </div>
            ))}
          </div>
          <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontWeight: 800, fontSize: 14 }}>Urgent?</span>
            <a className="btn btn-ghost btn-sm" href="https://wa.me/40700000000" target="_blank" rel="noreferrer">Scrie-ne pe WhatsApp</a>
          </div>
        </aside>
      </div>

      <NewThreadModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={(tid) => { setNewOpen(false); navigate(`/suport/${tid}`); }} />
    </div>
  );
}

function NewThreadModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const [subject, setSubject] = useState('');
  const [subjectKind, setSubjectKind] = useState('altceva');
  const [message, setMessage] = useState('');
  const invalidate = useInvalidate();
  const toast = useToast();
  const mut = useMutation({
    mutationFn: () => createSupportThread({ subject, subjectKind, message }),
    onSuccess: (res) => {
      invalidate([qk.support]);
      toast.push({ kind: 'ok', title: 'Mesaj trimis' });
      onCreated(res.thread.id);
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });
  return (
    <Modal open={open} onClose={onClose} title="Scrie-ne">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {SUBJECTS.map((s) => (
            <button key={s.value} className="chip" data-on={subjectKind === s.value} onClick={() => setSubjectKind(s.value)}>{s.label}</button>
          ))}
        </div>
        <label className="label">Subiect<input className="input" data-testid="thread-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="ex. Nu am primit factura" /></label>
        <label className="label">Mesaj<textarea className="input" data-testid="thread-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} /></label>
        <button className="btn btn-primary" data-testid="thread-submit" onClick={() => mut.mutate()} disabled={mut.isPending || subject.trim().length < 2 || message.trim().length < 2}>
          {mut.isPending ? <Spinner /> : 'Trimite mesajul'}
        </button>
      </div>
    </Modal>
  );
}

function ThreadView({ id }: { id: string }) {
  const navigate = useNavigate();
  const thread = useSupportThread(id);
  const invalidate = useInvalidate();
  const toast = useToast();
  const [body, setBody] = useState('');

  useEffect(() => {
    void markSupportRead(id).then(() => invalidate([qk.support]));
  }, [id]);

  const reply = useMutation({
    mutationFn: () => replySupport(id, { body: body.trim() }),
    onSuccess: () => {
      invalidate([qk.supportThread(id), qk.support]);
      setBody('');
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });

  if (thread.isLoading) return <Skeleton height={260} />;
  const t = thread.data?.thread;
  const messages = thread.data?.messages ?? [];

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <button onClick={() => navigate('/suport')} style={{ fontSize: 13, fontWeight: 800, color: 'var(--muted)' }}>← Toate conversațiile</button>
      <h1 className="bricolage" style={{ fontWeight: 800, fontSize: 24, margin: '8px 0 16px' }}>{t?.subject}</h1>
      <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div data-testid="thread-messages" style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 420, overflow: 'auto' }}>
          {messages.map((m) => (
            <div key={m.id} style={{ display: 'flex', justifyContent: m.sender === 'client' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '82%', padding: '11px 13px', borderRadius: 13, fontSize: 14, background: m.sender === 'client' ? 'var(--accent)' : 'var(--bg2)', color: m.sender === 'client' ? 'var(--accent-ink)' : 'var(--ink)' }}>
                {m.body}
                <div style={{ fontSize: 10.5, opacity: 0.7, marginTop: 4 }}>{timeAgo(m.createdAt)}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
          <input className="input" data-testid="reply-input" value={body} placeholder="Scrie un mesaj…" onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && body.trim() && reply.mutate()} />
          <button className="btn btn-primary btn-sm" data-testid="reply-send" onClick={() => reply.mutate()} disabled={reply.isPending || !body.trim()}>
            {reply.isPending ? <Spinner /> : 'Trimite'}
          </button>
        </div>
      </div>
    </div>
  );
}
